import { Button, message, Space, Tag, Modal} from 'antd';
import axios from 'axios';
import * as Component from 'components';
import { BASE_URL, VIDEO_ASSET_API_PATH } from 'constants/api';
import React, { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { videoService } from 'service/api/videoService';
import { TableColumnInterface } from 'type/tableType';
import { VideoType } from 'type/videoType';

export interface VideoAssetType {
  id: number;
  name: string;
  image?: string;
  source: 'inhouse' | 'mediahold' | string;
  property?: string;
  status: 'processing' | 'completed' | 'failed' | string;
  created: string;
  updated: string;
  file_size?: number;
  duration?: number;
}

interface S3Status {
  exists: boolean;
  size?: number;
  last_modified?: string;
  content_type?: string;
  etag?: string;
}

export default function Assets(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const [s3StatusMap, setS3StatusMap] = useState<Record<number, S3Status>>({});
  const [loadingS3Status, setLoadingS3Status] = useState<Record<number, boolean>>({});

  const getS3KeyFromFileUrl = (fileUrl: string): string | null => {
    try {
      const url = new URL(fileUrl);
      const pathParts = url.pathname.split('/');
      const prodIndex = pathParts.indexOf('prod');

      if (prodIndex === -1) {
        return null;
      }

      const prodPath = pathParts.slice(prodIndex).join('/');
      return `s3://ucms-content-prod/${prodPath}`;
    } catch {
      return null;
    }
  };

  const fetchS3Status = async (videoId: number, s3Key: string) => {
    try {
      setLoadingS3Status(prev => ({ ...prev, [videoId]: true }));
      const response = await axios.post(`${BASE_URL}${VIDEO_ASSET_API_PATH}${videoId}/s3-status/`, {
        s3_key: s3Key,
      });
      setS3StatusMap(prev => ({ ...prev, [videoId]: response.data }));
    } catch (error: any) {
      console.error('S3 status fetch error:', error);
      setS3StatusMap(prev => ({
        ...prev,
        [videoId]: { exists: false },
      }));
    } finally {
      setLoadingS3Status(prev => ({ ...prev, [videoId]: false }));
    }
  };

  const fetchAssets = useCallback(
    async (searchParams: URLSearchParams): Promise<{ data: VideoType[]; count: number }> => {
      searchParams.set('search', searchParams.get('search') || '');

      // Set default page_size to 10 if not specified
      if (!searchParams.get('page_size')) {
        searchParams.set('page_size', '100');
      }

      // Add filter parameters if they exist
      if (searchParams.get('ingest_source')) {
        searchParams.set('ingest_source', searchParams.get('ingest_source') || '');
      }
      if (searchParams.get('is_assigned')) {
        searchParams.set('is_assigned', searchParams.get('is_assigned') || '');
      }
      if (searchParams.get('status')) {
        searchParams.set('status', searchParams.get('status') || '');
      }

      const videos = await videoService.fetchVideos(searchParams);

      // Fetch S3 status for all videos at once (wait until complete)
      const videosToCheck = videos.data.filter(video => video.file_url && !s3StatusMap[video.id]);

      if (videosToCheck.length > 0) {
        // Wait until S3 status check completes
        const results = await Promise.all(
          videosToCheck.map(async video => {
            const s3Key = getS3KeyFromFileUrl(video.file_url!);
            if (!s3Key) return null;

            try {
              const response = await axios.post(
                `${BASE_URL}${VIDEO_ASSET_API_PATH}${video.id}/s3-status/`,
                { s3_key: s3Key }
              );
              return { videoId: video.id, status: response.data };
            } catch (error: any) {
              console.error(`S3 status fetch error for video ${video.id}:`, error);
              return { videoId: video.id, status: { exists: false } };
            }
          })
        );

        // Update all S3 statuses at once
        const newS3StatusMap: Record<number, S3Status> = {};
        results.forEach(result => {
          if (result) {
            newS3StatusMap[result.videoId] = result.status;
          }
        });
        setS3StatusMap(prev => ({ ...prev, ...newS3StatusMap }));
      }

      return {
        data: videos.data,
        count: videos.count,
      };
    },
    []
  );

  const handleDownload = async (record: VideoType) => {
    try {
      if (!record.file_url) {
        Modal.error({ title: 'Error', content: 'File URL is missing.', okText: 'Confirm' });
        return;
      }

      const fileUrl = new URL(record.file_url);
      const pathParts = fileUrl.pathname.split('/');
      const prodIndex = pathParts.indexOf('prod');

      if (prodIndex === -1) {
        Modal.error({ title: 'Error', content: 'Could not find prod path in URL.', okText: 'Confirm' });
        return;
      }

      const prodPath = pathParts.slice(prodIndex).join('/');
      const s3Key = `s3://ucms-content-prod/${prodPath}`;

      message.loading({ content: 'Generating Presigned URL...', key: 'download', duration: 0 });

      const response = await axios.post(
        `${BASE_URL}${VIDEO_ASSET_API_PATH}${record.id}/download-presigned-url/`,
        { s3_key: s3Key }
      );

      const presignedUrl = response.data.presigned_url || response.data.url;

      if (!presignedUrl) {
        Modal.error({ title: 'Error', content: 'Could not retrieve Presigned URL.', okText: 'Confirm' });
        return;
      }

      message.success({ content: 'Starting download...', key: 'download' });

      // Pass to browser default download
      const a = document.createElement('a');
      a.href = presignedUrl;

      // download attribute is often ignored for cross-origin URLs
      // Optional (filename usually depends on server Content-Disposition)
      // a.download = prodPath.split('/').pop() || record.file_name || 'download';

      a.target = '_blank'; // Handle in new tab (browser download manager takes over)
      a.rel = 'noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error: any) {
      console.error('Download error:', error);
      Modal.error({ title: 'Error', content: `Download failed: ${error.message || 'Unknown error'}`, okText: 'Confirm' });
    }
  };

  const columns: TableColumnInterface<VideoType>[] = [
    {
      title: 'File Name',
      dataIndex: 'file_name',
      key: 'file_name',
      width: '60%',
      sorter: true as any,
      render: (_: string, record: VideoType) => {
        const getFileNameFromUrl = (url: string): string => {
          try {
            const urlObj = new URL(url);
            const pathParts = urlObj.pathname.split('/');
            return pathParts[pathParts.length - 1] || url;
          } catch {
            // Extract directly from path if URL parsing fails
            const pathParts = url.split('/');
            return pathParts[pathParts.length - 1] || url;
          }
        };

        const fileName = record.file_url
          ? getFileNameFromUrl(record.file_url)
          : record.file_name || '-';

        return (
          <div style={{ maxWidth: '600px', wordBreak: 'break-word' }}>
            <div style={{ color: 'black', fontSize: '12px', marginTop: '2px' }}>{fileName}</div>
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '15%',
      render: (_: string, record: VideoType) => {
        const s3Status = s3StatusMap[record.id];
        const isLoading = loadingS3Status[record.id];

        if (isLoading) {
          return <Tag color="processing">Checking...</Tag>;
        }

        if (!s3Status) {
          if (record.file_url) {
            const s3Key = getS3KeyFromFileUrl(record.file_url);
            if (s3Key) {
              return (
                <Button size="small" type="link" onClick={() => fetchS3Status(record.id, s3Key)}>
                  Check S3
                </Button>
              );
            }
          }
          return <Tag>-</Tag>;
        }

        return (
          <Space direction="vertical" size="small">
            <Tag color={s3Status.exists ? 'green' : 'red'}>
              {s3Status.exists ? 'Uploaded' : 'Failed'}
            </Tag>
            {s3Status.exists && s3Status.size && (
              <div style={{ fontSize: '11px', color: '#666' }}>
                Size: {(s3Status.size / (1024 * 1024 * 1024)).toFixed(2)} GB
              </div>
            )}
            {s3Status.exists && s3Status.last_modified && (
              <div style={{ fontSize: '11px', color: '#666' }}>
                Modified: {new Date(s3Status.last_modified).toLocaleDateString()}
              </div>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Download',
      dataIndex: 'download',
      key: 'download',
      width: '10%',
      render: (_: string, record: VideoType) => (
        <Button type="primary" onClick={() => handleDownload(record)}>
          Download
        </Button>
      ),
    },
  ];

  return (
    <Component.BasePageComponent>
      <Component.TableComponent<VideoType>
        name="Video Assets"
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        dataFetch={fetchAssets}
        columns={columns}
      />
    </Component.BasePageComponent>
  );
}
